import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import { toast } from 'sonner'
import {
    TrendingUp,
    Package,
    BarChart3,
    Search,
    RefreshCw,
    Eye,
    AlertTriangle,
    CheckCircle,
    Clock,
    DollarSign,
    X,
    Send
} from 'lucide-react'

interface VelocityProduct {
    id: string
    sku: string
    name: string
    stock: number
    price: string
    brand: string
    category: string
    image1?: string
    ageDays: number
    ageCategory: string
    totalSales: number
    totalRevenue: number
    sales30Days: number
    sales14Days: number
    sales7Days: number
    orderCount: number
    lastSaleDate: string | null
    stockStatus: string
    stockDepletionRate: number | null
    dailyVelocity: number
    recentVelocity: number
    momentum: number
    finalVelocityScore: number
    velocityRank: number
}

interface VelocityResponse {
    rankings: VelocityProduct[]
    stats: {
        totalProducts: number
        inStock: number
        outOfStock: number
        lowStock: number
        topPerformers: VelocityProduct[]
        lastUpdated: string
    }
    total: number
}

export default function VelocityAnalysisPage() {
    const [searchQuery, setSearchQuery] = useState('')
    const [stockFilter, setStockFilter] = useState<'all' | 'in_stock' | 'out_of_stock' | 'low_stock'>('all')
    const [ageFilter, setAgeFilter] = useState<'all' | 'Yeni' | 'Genç' | 'Olgun' | 'Eski'>('all')
    const [sortBy, setSortBy] = useState<'finalVelocityScore' | 'totalSales' | 'recentVelocity' | 'momentum'>('finalVelocityScore')
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
    const [selectedImage, setSelectedImage] = useState<string | null>(null)
    const [isImageModalOpen, setIsImageModalOpen] = useState(false)

    const { accessToken } = useAuthStore()

    // Velocity analizi verilerini getir
    const { data: velocityData, refetch: refetchVelocity, isLoading: isLoadingVelocity, error: velocityError } = useQuery<VelocityResponse>({
        queryKey: ['velocityAnalysis'],
        queryFn: async () => {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/velocity/analysis`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (!response.ok) {
                throw new Error('Velocity analizi yüklenemedi')
            }

            return response.json()
        },
        enabled: !!accessToken,
        staleTime: 5 * 60 * 1000, // 5 dakika cache
        gcTime: 10 * 60 * 1000, // 10 dakika garbage collection
    })

    // Filtrelenmiş veriler
    const [filteredData, setFilteredData] = useState<VelocityProduct[]>([])

    useEffect(() => {
        if (velocityData) {
            let filtered = [...velocityData.rankings]

            // Arama filtresi
            if (searchQuery) {
                filtered = filtered.filter(item =>
                    item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.brand.toLowerCase().includes(searchQuery.toLowerCase())
                )
            }

            // Stok filtresi
            if (stockFilter === 'in_stock') {
                filtered = filtered.filter(item => item.stock > 0)
            } else if (stockFilter === 'out_of_stock') {
                filtered = filtered.filter(item => item.stock === 0)
            } else if (stockFilter === 'low_stock') {
                filtered = filtered.filter(item => item.stock > 0 && item.stock <= 5)
            }

            // Yaş filtresi
            if (ageFilter !== 'all') {
                filtered = filtered.filter(item => item.ageCategory === ageFilter)
            }

            // Sıralama
            filtered.sort((a, b) => {
                let aValue, bValue
                switch (sortBy) {
                    case 'totalSales':
                        aValue = a.totalSales
                        bValue = b.totalSales
                        break
                    case 'recentVelocity':
                        aValue = a.recentVelocity
                        bValue = b.recentVelocity
                        break
                    case 'momentum':
                        aValue = a.momentum
                        bValue = b.momentum
                        break
                    default:
                        aValue = a.finalVelocityScore
                        bValue = b.finalVelocityScore
                }

                return sortOrder === 'desc' ? bValue - aValue : aValue - bValue
            })

            setFilteredData(filtered)
        }
    }, [velocityData, searchQuery, stockFilter, ageFilter, sortBy, sortOrder])

    const handleRefresh = async () => {
        const timeout = 5 * 60 * 1000 // 5 dakika timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => {
            controller.abort()
        }, timeout)

        try {
            toast.loading('Velocity analizi yeniden hesaplanıyor... (Bu işlem birkaç dakika sürebilir)')

            // Backend'de yeniden hesaplama yap
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/velocity/recalculate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                signal: controller.signal
            })

            clearTimeout(timeoutId)

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.error?.message || 'Yeniden hesaplama başarısız')
            }

            // Response'u bekle (backend işlemi tamamlasın)
            const result = await response.json().catch(() => ({}))

            // Hesaplama tamamlandıktan sonra verileri yeniden çek
            await refetchVelocity()

            toast.dismiss()
            toast.success('Velocity analizi başarıyla güncellendi!')
        } catch (error) {
            clearTimeout(timeoutId)
            toast.dismiss()
            
            let errorMessage = 'Velocity analizi güncellenirken hata oluştu'
            if (error instanceof Error) {
                if (error.name === 'AbortError' || error.message.includes('zaman aşımı')) {
                    errorMessage = 'İşlem zaman aşımına uğradı. Hesaplama arka planda devam ediyor olabilir. Lütfen birkaç dakika sonra sayfayı yenileyin.'
                } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                    errorMessage = 'Ağ hatası oluştu. İşlem arka planda devam ediyor olabilir. Lütfen birkaç dakika sonra sayfayı yenileyin.'
                } else {
                    errorMessage = error.message || errorMessage
                }
            }
            
            toast.error(errorMessage)
            console.error('Refresh error:', error)
        }
    }

    const handleSendToTelegram = async () => {
        if (!velocityData) {
            toast.error('Velocity verisi yüklenmedi')
            return
        }

        try {
            toast.loading('Telegram\'a gönderiliyor...')

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/telegram/send-velocity`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    velocityData: {
                        stats: velocityData.stats,
                        rankings: velocityData.rankings.slice(0, 10) // Top 10 ürün
                    }
                })
            })

            const data = await response.json()

            if (response.ok && data.success) {
                toast.dismiss()
                toast.success('Velocity analizi Telegram\'a gönderildi!')
            } else {
                toast.dismiss()
                toast.error(data.error || 'Telegram\'a gönderim başarısız')
            }
        } catch (error) {
            toast.dismiss()
            toast.error('Telegram\'a gönderim sırasında hata oluştu')
            console.error('Telegram send error:', error)
        }
    }

    const handleSendProductToTelegram = async (product: VelocityProduct) => {
        try {
            toast.loading(`${product.name} Telegram'a gönderiliyor...`)

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/telegram/send-product`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    productId: product.id,
                    source: 'velocity' // Velocity sayfasından gönderildiğini belirt
                })
            })

            const data = await response.json()

            if (response.ok && data.success) {
                toast.dismiss()
                toast.success(`${product.name} Telegram'a gönderildi!`)
            } else {
                toast.dismiss()
                toast.error(data.error || 'Telegram\'a gönderim başarısız')
            }
        } catch (error) {
            toast.dismiss()
            toast.error('Telegram\'a gönderim sırasında hata oluştu')
            console.error('Telegram send error:', error)
        }
    }

    const getStockStatusColor = (status: string) => {
        switch (status) {
            case 'Tükendi': return 'text-red-600'
            case 'Az Stok': return 'text-orange-600'
            case 'Orta Stok': return 'text-yellow-600'
            case 'Bol Stok': return 'text-green-600'
            default: return 'text-gray-600'
        }
    }

    const getStockStatusIcon = (status: string) => {
        switch (status) {
            case 'Tükendi': return <AlertTriangle className="w-4 h-4 text-red-600" />
            case 'Az Stok': return <AlertTriangle className="w-4 h-4 text-orange-600" />
            case 'Orta Stok': return <Clock className="w-4 h-4 text-yellow-600" />
            case 'Bol Stok': return <CheckCircle className="w-4 h-4 text-green-600" />
            default: return <Package className="w-4 h-4 text-gray-600" />
        }
    }

    if (isLoadingVelocity) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-700">Velocity Analizi Yükleniyor...</h2>
                    <p className="text-gray-500">35,000+ ürün analiz ediliyor, lütfen bekleyin</p>
                </div>
            </div>
        )
    }

    if (velocityError) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Hata!</h2>
                    <p className="text-gray-600 mb-4">Velocity analizi yüklenirken bir hata oluştu</p>
                    <Button onClick={handleRefresh} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Tekrar Dene
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-3 sm:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                                <span className="hidden sm:inline">Gerçek Velocity Analizi</span>
                                <span className="sm:hidden">Velocity</span>
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600 mt-2">
                                Sipariş istatistikleri ve stok tükenme hızı analizi
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                            <div className="text-left sm:text-right">
                                <p className="text-xs sm:text-sm text-gray-500">Son Güncelleme</p>
                                <p className="text-sm sm:text-base font-semibold">{velocityData?.stats.lastUpdated ? new Date(velocityData.stats.lastUpdated).toLocaleString('tr-TR') : 'Bilinmiyor'}</p>
                            </div>
                            <Button onClick={handleRefresh} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Yenile
                            </Button>
                        </div>
                    </div>

                    {/* İstatistikler */}
                    {velocityData?.stats && (
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                            <Card>
                                <CardContent className="p-3 sm:p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs sm:text-sm font-medium text-gray-600">Toplam</p>
                                            <p className="text-lg sm:text-2xl font-bold text-blue-600">{velocityData.stats.totalProducts.toLocaleString()}</p>
                                        </div>
                                        <Package className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-3 sm:p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs sm:text-sm font-medium text-gray-600">Stokta</p>
                                            <p className="text-lg sm:text-2xl font-bold text-green-600">{velocityData.stats.inStock.toLocaleString()}</p>
                                        </div>
                                        <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-3 sm:p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs sm:text-sm font-medium text-gray-600">Tükendi</p>
                                            <p className="text-lg sm:text-2xl font-bold text-red-600">{velocityData.stats.outOfStock.toLocaleString()}</p>
                                        </div>
                                        <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-3 sm:p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs sm:text-sm font-medium text-gray-600">Az Stok</p>
                                            <p className="text-lg sm:text-2xl font-bold text-orange-600">{velocityData.stats.lowStock.toLocaleString()}</p>
                                        </div>
                                        <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>

                {/* Filtreler */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Search className="w-5 h-5" />
                            Filtreler ve Arama
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <Label htmlFor="search">Ürün Ara</Label>
                                <Input
                                    id="search"
                                    placeholder="SKU, isim veya marka ara..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="stockFilter">Stok Durumu</Label>
                                <select
                                    id="stockFilter"
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={stockFilter}
                                    onChange={(e) => setStockFilter(e.target.value as any)}
                                >
                                    <option value="all">Tüm Stoklar</option>
                                    <option value="in_stock">Stokta</option>
                                    <option value="out_of_stock">Tükendi</option>
                                    <option value="low_stock">Az Stok (≤5)</option>
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="ageFilter">Yaş Kategorisi</Label>
                                <select
                                    id="ageFilter"
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={ageFilter}
                                    onChange={(e) => setAgeFilter(e.target.value as any)}
                                >
                                    <option value="all">Tüm Kategoriler</option>
                                    <option value="Yeni">Yeni (≤30 gün)</option>
                                    <option value="Genç">Genç (31-90 gün)</option>
                                    <option value="Olgun">Olgun (91-365 gün)</option>
                                    <option value="Eski">Eski (&gt;365 gün)</option>
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="sortBy">Sıralama</Label>
                                <select
                                    id="sortBy"
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value as any)}
                                >
                                    <option value="finalVelocityScore">Velocity Skoru</option>
                                    <option value="totalSales">Toplam Satış</option>
                                    <option value="recentVelocity">Son 14 Gün</option>
                                    <option value="momentum">Momentum</option>
                                </select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Sonuçlar */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5" />
                            Velocity Sıralaması ({filteredData.length} ürün)
                        </CardTitle>
                        <CardDescription>
                            Sipariş istatistikleri ve stok tükenme hızına göre sıralanmış ürün listesi
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Masaüstü Tablo Görünümü */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left p-2">Sıra</th>
                                        <th className="text-left p-2">Görsel</th>
                                        <th className="text-left p-2">SKU</th>
                                        <th className="text-left p-2">Ürün Adı</th>
                                        <th className="text-left p-2">Stok</th>
                                        <th className="text-left p-2">Toplam Satış</th>
                                        <th className="text-left p-2">Son 14 Gün</th>
                                        <th className="text-left p-2">Velocity Skoru</th>
                                        <th className="text-left p-2">Momentum</th>
                                        <th className="text-left p-2">Tükenme Süresi</th>
                                        <th className="text-left p-2">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.slice(0, 100).map((product) => (
                                        <tr key={product.id} className="border-b hover:bg-gray-50">
                                            <td className="p-2 font-semibold text-blue-600">#{product.velocityRank}</td>
                                            <td className="p-2">
                                                {product.image1 ? (
                                                    <img
                                                        src={product.image1}
                                                        alt={product.name}
                                                        className="w-12 h-12 object-cover rounded cursor-pointer"
                                                        onClick={() => {
                                                            setSelectedImage(product.image1!)
                                                            setIsImageModalOpen(true)
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                                        <Package className="w-6 h-6 text-gray-400" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 font-mono text-sm">{product.sku}</td>
                                            <td className="p-2 max-w-xs">
                                                <div className="truncate" title={product.name}>
                                                    {product.name}
                                                </div>
                                                <div className="text-xs text-gray-500">{product.brand}</div>
                                            </td>
                                            <td className="p-2">
                                                <div className="flex items-center gap-2">
                                                    {getStockStatusIcon(product.stockStatus)}
                                                    <span className={`font-semibold ${getStockStatusColor(product.stockStatus)}`}>
                                                        {product.stock}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-500">{product.stockStatus}</div>
                                            </td>
                                            <td className="p-2">
                                                <div className="font-semibold">{product.totalSales}</div>
                                                <div className="text-xs text-gray-500">{product.orderCount} sipariş</div>
                                            </td>
                                            <td className="p-2">
                                                <div className="font-semibold">{product.sales14Days}</div>
                                                <div className="text-xs text-gray-500">
                                                    {product.recentVelocity.toFixed(1)}/gün
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="font-semibold text-blue-600">
                                                    {product.finalVelocityScore.toFixed(2)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {product.ageCategory} ({product.ageDays} gün)
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className={`font-semibold ${product.momentum > 1 ? 'text-green-600' : product.momentum < 1 ? 'text-red-600' : 'text-gray-600'}`}>
                                                    {product.momentum.toFixed(2)}x
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {product.momentum > 1 ? 'Hızlanıyor' : product.momentum < 1 ? 'Yavaşlıyor' : 'Sabit'}
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                {product.stockDepletionRate ? (
                                                    <div className="text-orange-600 font-semibold">
                                                        {Math.ceil(product.stockDepletionRate)} gün
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-500">-</div>
                                                )}
                                            </td>
                                            <td className="p-2">
                                                <button
                                                    onClick={() => handleSendProductToTelegram(product)}
                                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                                    title="Telegram'a gönder"
                                                >
                                                    <Send className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobil Kart Görünümü */}
                        <div className="md:hidden space-y-4">
                            {filteredData.slice(0, 100).map((product) => (
                                <Card key={product.id} className="border-l-4" style={{ borderLeftColor: product.stock === 0 ? '#ef4444' : product.stock <= 5 ? '#f97316' : '#10b981' }}>
                                    <CardContent className="p-4">
                                        <div className="flex gap-3">
                                            {/* Görsel */}
                                            <div className="flex-shrink-0">
                                                {product.image1 ? (
                                                    <img
                                                        src={product.image1}
                                                        alt={product.name}
                                                        className="w-20 h-20 object-cover rounded cursor-pointer"
                                                        onClick={() => {
                                                            setSelectedImage(product.image1!)
                                                            setIsImageModalOpen(true)
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center">
                                                        <Package className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Ürün Bilgileri */}
                                            <div className="flex-1 min-w-0">
                                                {/* Sıra ve Ürün Adı */}
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-xs font-bold text-white bg-blue-600 px-2 py-0.5 rounded">
                                                                #{product.velocityRank}
                                                            </span>
                                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getStockStatusColor(product.stockStatus)}`}>
                                                                {product.stockStatus}
                                                            </span>
                                                        </div>
                                                        <h3 className="font-semibold text-sm line-clamp-2 text-gray-900">
                                                            {product.name}
                                                        </h3>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {product.sku} • {product.brand}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() => handleSendProductToTelegram(product)}
                                                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                        title="Telegram'a gönder"
                                                    >
                                                        <Send className="w-5 h-5" />
                                                    </button>
                                                </div>

                                                {/* Metrikler Grid */}
                                                <div className="grid grid-cols-2 gap-2 mt-3">
                                                    <div className="bg-blue-50 p-2 rounded">
                                                        <p className="text-xs text-gray-600">Velocity Skoru</p>
                                                        <p className="text-sm font-bold text-blue-600">
                                                            {product.finalVelocityScore.toFixed(1)}
                                                        </p>
                                                    </div>
                                                    <div className="bg-green-50 p-2 rounded">
                                                        <p className="text-xs text-gray-600">Toplam Satış</p>
                                                        <p className="text-sm font-bold text-green-600">
                                                            {product.totalSales} adet
                                                        </p>
                                                    </div>
                                                    <div className="bg-purple-50 p-2 rounded">
                                                        <p className="text-xs text-gray-600">Son 14 Gün</p>
                                                        <p className="text-sm font-bold text-purple-600">
                                                            {product.sales14Days} adet
                                                        </p>
                                                    </div>
                                                    <div className="bg-orange-50 p-2 rounded">
                                                        <p className="text-xs text-gray-600">Stok</p>
                                                        <p className={`text-sm font-bold ${getStockStatusColor(product.stockStatus)}`}>
                                                            {product.stock} adet
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Ek Bilgiler */}
                                                <div className="flex items-center justify-between mt-3 pt-2 border-t">
                                                    <div className="flex items-center gap-3 text-xs text-gray-600">
                                                        <span>
                                                            🔥 {product.momentum.toFixed(2)}x
                                                        </span>
                                                        <span>
                                                            📅 {product.ageCategory}
                                                        </span>
                                                        {product.stockDepletionRate && (
                                                            <span className="text-orange-600">
                                                                ⏱️ {Math.ceil(product.stockDepletionRate)}g
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Görsel Modal */}
            {isImageModalOpen && selectedImage && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={() => setIsImageModalOpen(false)}
                >
                    <div 
                        className="relative bg-white rounded-lg shadow-2xl max-w-[90vw] max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Çarpı Butonu */}
                        <button
                            onClick={() => setIsImageModalOpen(false)}
                            className="absolute -top-3 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 z-10 shadow-lg transition-colors"
                            aria-label="Kapat"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        
                        {/* Görsel */}
                        <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
                            <img 
                                src={selectedImage} 
                                alt="Ürün Görseli" 
                                className="max-w-full max-h-[calc(90vh-80px)] object-contain rounded"
                            />
                        </div>
                        
                        {/* Alt Buton */}
                        <div className="p-4 border-t">
                            <Button
                                onClick={() => setIsImageModalOpen(false)}
                                className="w-full bg-gray-600 hover:bg-gray-700"
                            >
                                Kapat
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
